import { useState } from "react";
import { api } from "../lib/api";
import { Spark } from "./Spark";
import { Lock, ArrowRight, Loader2 } from "lucide-react";

// Mini-questionário da primeira entrada: 3 perguntas rápidas que já preenchem
// o essencial do perfil e ligam a personalização desde o primeiro uso. Pulável.
const PERGUNTAS = [
  {
    campo: "como_aprendo",
    titulo: "Como você aprende melhor?",
    opcoes: [
      "Com exemplos práticos antes da teoria",
      "Passo a passo, com calma",
      "Com resumos e esquemas",
      "Me testando com questões",
    ],
  },
  {
    campo: "o_que_trava",
    titulo: "O que mais te trava na hora de estudar?",
    opcoes: [
      "Entender a teoria",
      "Erros bobos de conta / atenção",
      "Interpretar enunciados",
      "Foco — começar a estudar",
    ],
  },
  {
    campo: "objetivos",
    titulo: "Qual seu objetivo agora?",
    opcoes: [
      "Ir bem nas provas da escola",
      "ENEM / vestibular",
      "Recuperar notas",
      "Aprender de verdade",
    ],
  },
];

// Primeira sugestão personalizada: transforma o que o aluno respondeu numa
// pergunta pronta pro chat — ele só completa com a matéria e envia.
function gerarSugestao(r) {
  const trava = r.o_que_trava || "";
  if (trava.includes("teoria")) return "Me explica do zero, do jeito mais simples possível, o tópico que mais tá me travando agora: ";
  if (trava.includes("conta")) return "Me passa questões rápidas de treino pra eu parar de errar bobeira nas contas. A matéria é: ";
  if (trava.includes("enunciado")) return "Quero treinar interpretação: me dá uma questão de enunciado grande e vamos destrinchar juntos. Matéria: ";
  if (trava.includes("Foco")) return "Me ajuda a começar AGORA: monta comigo um mini-plano de 25 minutos de estudo. A matéria é: ";
  const obj = r.objetivos || "";
  if (obj.includes("ENEM")) return "Bora começar a preparação pro ENEM: qual matéria você acha que eu devia atacar primeiro?";
  if (obj.includes("Recuperar")) return "Preciso recuperar nota. A matéria que tá pior é: ";
  return "";
}

export default function Onboarding({ onDone }) {
  const [step, setStep] = useState(0);
  const [respostas, setRespostas] = useState({});
  const [busy, setBusy] = useState(false);

  const finalizar = async (dados, pulou = false) => {
    setBusy(true);
    try {
      await api.put("/profile", { ...dados, onboarding_done: true });
    } catch {
      // sem drama: o aluno pode preencher depois em Meu Perfil
    }
    // Quem respondeu ganha uma primeira sugestão personalizada direto no chat
    onDone(pulou ? "" : gerarSugestao(dados));
  };

  const escolher = (opcao) => {
    const novas = { ...respostas, [PERGUNTAS[step].campo]: opcao };
    setRespostas(novas);
    if (step < PERGUNTAS.length - 1) {
      setStep(step + 1);
    } else {
      finalizar(novas);
    }
  };

  const pular = () => finalizar(respostas, true);

  const q = PERGUNTAS[step];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div data-testid="onboarding-modal" className="w-full max-w-md rounded-2xl border border-border/60 bg-card p-6 sm:p-8 space-y-6 shadow-2xl shadow-violet-500/10 anim-msg">
        <div className="flex flex-col items-center text-center gap-3">
          <Spark className="h-12 w-12" />
          <div>
            <h2 className="text-xl font-bold tracking-tight">
              {step === 0 ? "Seu tutor quer te conhecer" : q.titulo}
            </h2>
            {step === 0 && (
              <p className="mt-1.5 text-sm text-muted-foreground">
                3 perguntas rápidas e ele já começa a ensinar do SEU jeito.
              </p>
            )}
          </div>
        </div>

        {step === 0 && <p className="text-center font-semibold text-sm">{q.titulo}</p>}

        <div className="space-y-2">
          {q.opcoes.map((op) => (
            <button
              key={op}
              type="button"
              disabled={busy}
              onClick={() => escolher(op)}
              className="w-full rounded-xl border border-border bg-secondary hover:border-violet-500/50 hover:bg-violet-500/10 px-4 py-3 text-sm text-left transition duration-200 disabled:opacity-60"
            >
              {op}
            </button>
          ))}
        </div>

        {/* progresso */}
        <div className="flex items-center justify-center gap-1.5">
          {PERGUNTAS.map((_, i) => (
            <span
              key={i}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                i === step ? "w-6 bg-violet-500" : i < step ? "w-1.5 bg-violet-500/60" : "w-1.5 bg-border"
              }`}
            />
          ))}
        </div>

        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1.5">
            <Lock className="h-3 w-3" /> Privado — só o seu tutor vê
          </span>
          <button
            type="button"
            data-testid="onboarding-skip"
            onClick={pular}
            disabled={busy}
            className="inline-flex items-center gap-1 hover:text-foreground transition-colors duration-200 disabled:opacity-60"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <>Pular <ArrowRight className="h-3 w-3" /></>}
          </button>
        </div>
      </div>
    </div>
  );
}
